import { CloseScrollStrategy, Overlay } from '@angular/cdk/overlay';
import { HttpBackend } from '@angular/common/http';
import { ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_MENU_SCROLL_STRATEGY } from '@angular/material/menu';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { ServiceWorkerModule } from '@angular/service-worker';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { io } from 'socket.io-client';

import {
  DswLoginComponent,
  DswAppSwitcherComponent,
  DswSupportLinkComponent,
} from '@vi-succ/design-system-web';

import { AppStoreModule } from '@store';

import { environment } from '../environments/environment';

import { BookingModule } from './+booking/booking.module';
import { DemoSettingsModule } from './+demo-settings/demo-settings.module';
import { GatewayCommissioningModule } from './+gateway-commissioning/gateway-commissioning.module';
import { HomeModule } from './+home/home.module';
import { InstallationsModule } from './+installations/installations.module';
import { InvitationsModule } from './+invitations/invitations.module';
import { LegalInformationModule } from './+legal-information/legal-information.module';
import { SearchModule } from './+search/search.module';
import { SettingsModule } from './+settings/settings.module';
import { UpdatesModule } from './+updates/updates.module';
import { MaterialDemoModule } from './@material/material-demo.module';
import { AppComponent } from './app.component';
import { AppRouting } from './app.routing';
import { DynamicLocaleId } from './dynamic-locale-Id';
import { SentryErrorHandler } from './sentry-error-handler';
import { SharedDependenciesModule } from './shared';
import { AppPaginatorIntl } from './shared/common/app-paginator-intl';
import { TranslationConfigService } from './shared/services/translation-config.service';
import { ServiceTicketComponent } from './shared/+service-ticket/service-ticket.component';
import { ShepherdStepCounterComponent } from './shepherd/components/shepherd-step-counter/shepherd-step-counter.component';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { ViHeaderDomainModule } from '@vi-succ/vi-header-domain';
import { OAuthModule, OAuthStorage } from 'angular-oauth2-oidc';

export function createTranslateLoader(http: HttpBackend): MultiTranslateHttpLoader {
  return new MultiTranslateHttpLoader(http, [
    // app i18n assets
    {
      prefix: './assets/i18n/',
      suffix: '.json?timestamp=' + new Date().getTime(),
    },
    // @vi-succ/design-system-web translations
    {
      prefix:
        'https://api.viessmann-climatesolutions.com/translations/v2/phraseapp-proxy/projects/ec38b083a23c58c604a49d7f443dd7f2/locales/',
      suffix: '/download?file_format=simple_json',
    },
  ]);
}
export function scrollFactory(overlay: Overlay): () => CloseScrollStrategy {
  return () => overlay.scrollStrategies.close();
}

// localStorage is not available during AOT build time
export function storageFactory(): OAuthStorage {
  return localStorage;
}

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRouting,
    AppStoreModule,
    SharedDependenciesModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpBackend],
      },
    }),
    SearchModule,
    HomeModule,
    InstallationsModule,
    BookingModule,
    InvitationsModule,
    DemoSettingsModule,
    SettingsModule,
    UpdatesModule,
    GatewayCommissioningModule,
    LegalInformationModule,
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
    ViHeaderDomainModule.forRoot({
      baseUrl: environment.headerModuleApiUrl,
      accountAppUrl: environment.accountApiUrl,
      appId: environment.oAuthConfig.clientId,
    }),
    OAuthModule.forRoot(),
    DswAppSwitcherComponent,
    DswLoginComponent,
    DswSupportLinkComponent,
    MaterialDemoModule,
    ServiceTicketComponent,
    ShepherdStepCounterComponent,
  ],
  declarations: [AppComponent],
  providers: [
    { provide: io, useValue: io },
    TranslationConfigService,
    {
      provide: ErrorHandler,
      useClass: SentryErrorHandler,
    },
    { provide: LOCALE_ID, useClass: DynamicLocaleId, deps: [TranslateService] }, // MAT_DATE_LOCALE use LOCALE_ID
    { provide: MAT_MENU_SCROLL_STRATEGY, useFactory: scrollFactory, deps: [Overlay] },
    { provide: ApplicationInsights, useValue: ApplicationInsights },
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { floatLabel: 'auto', subscriptSizing: 'dynamic' },
    },
    {
      provide: MatPaginatorIntl,
      useClass: AppPaginatorIntl,
      deps: [TranslateService],
    },
    { provide: OAuthStorage, useFactory: storageFactory },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
